import { Loader, Toast } from '@getvim/atomic-ui';
import { Provider } from '@getvim/internal-vim-os-sdk/types';
import React, { useCallback, useEffect, useState } from 'react';
import { useAnalytics } from '../../../analytics/AnalyticsProvider';
import { getContentSupplierId } from '../../../analytics/analyticsUtils';
import { iframeWidth, WIDGET_DEFAULT_WIDTH } from '../../../consts/widgetSize';
import { SMARTAppStatus, Tab, tabToSmartApp, CurrentContext } from '../../../types';
import { getParamFromUrlQueryString, createLogger } from '../../../utils';
import { ErrorScreen } from '../../errors/ErrorScreen';
import { ActionButton } from '../../action-button/ActionButton';
import SelectProvider from '../../select-provider/SelectProvider';
import { widgetListener } from './listeners/listener';
import { getSmartAppLaunchUrl } from './smartAppLauncher';
import './uhcSmartAppWrapper.less';
import { useVimUserMetadata, useApi } from '@getvim/internal-vim-os-sdk/react';
import { usePubSubService } from '../../../hooks/usePubSubService';
import { filterLogContext } from '../../../utils/filterLogContext';
const { createToast, ToastTypes } = Toast;

const priorAuthLogger = createLogger('UhcSmartAppWrapper');

export interface UhcSmartAppWrapperProps {
  tab: Tab;
  currentContext: CurrentContext;
  goToTab: (tab: Tab) => void;
  currentTab: Tab;
  updateLaunchedComponents: (tab: { [key: string]: boolean }) => void;
  shouldRelaunch?: boolean;
  isEligibilityFailed: boolean;
  displaySelectProviderScreen: boolean;
  confirmSelectedProvider: (selectedProvider: Provider) => CurrentContext;
  setSelectedProvider: (SelectOption: Provider) => void;
  selectedProvider: Provider | undefined;
  updateEligibility: (context: CurrentContext) => Promise<CurrentContext | undefined>;
  changeTabWidth: (tabWidth: string) => void;
}

const getLaunchId = (url: string): string | undefined => {
  if (!url) return;
  return getParamFromUrlQueryString(url, 'launch');
};

const UhcSmartAppWrapper: React.FC<UhcSmartAppWrapperProps> = ({
  tab,
  currentContext,
  goToTab,
  currentTab,
  updateLaunchedComponents,
  shouldRelaunch,
  isEligibilityFailed,
  displaySelectProviderScreen,
  confirmSelectedProvider,
  setSelectedProvider,
  updateEligibility,
  selectedProvider,
  changeTabWidth,
}: UhcSmartAppWrapperProps) => {
  const [iframeUrl, setIframeUrl] = useState<string | undefined>();
  const [hasError, setHasError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [flowSuccess, setFlowSuccess] = useState<boolean>(false);
  const [launchId, setLaunchId] = useState<string | undefined>();
  const [shouldSelectProvider, setShouldSelectProvider] = useState(displaySelectProviderScreen);
  const { srn, initPubSubService, shutdownPubSubService } = usePubSubService();
  const { copyToClipboard } = useApi();
  const { analyticsClient } = useAnalytics();
  const logContext = filterLogContext(currentContext);
  useEffect(() => {
    void (async () => {
      if (srn) {
        try {
          await copyToClipboard(`Pub UnitedHealth Primary Service Reference Number: ${srn}`);
        } catch {
          priorAuthLogger.error('copying service reference number to clipboard failed', {
            launchId,
            srn,
            ...logContext,
            noPHI: true,
          });
          analyticsClient.trackWithContext('copy_SRN_to_clipboard', {
            launchId,
            srn,
            status: SMARTAppStatus.Failure,
          });
          return;
        }

        priorAuthLogger.info('copying service reference number to clipboard succeed', {
          launchId,
          srn,
          ...logContext,
          noPHI: true,
        });
        analyticsClient.trackWithContext('copy_SRN_to_clipboard', {
          launchId,
          srn,
          status: SMARTAppStatus.Success,
        });
        createToast({
          title: 'Success!',
          message: 'Service Reference Number was copied to clipboard',
          type: ToastTypes.SUCCESS,
          html: true,
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srn]);

  const resetState = useCallback(() => {
    setFlowSuccess(false);
    setLoading(false);
    setHasError(false);
    setIframeUrl(undefined);
    setShouldSelectProvider(displaySelectProviderScreen);
    updateLaunchedComponents({ [tab]: false });
    shutdownPubSubService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displaySelectProviderScreen, tab, updateLaunchedComponents]);

  useEffect(() => {
    if (currentTab !== tab) return;
    if (!shouldSelectProvider) {
      changeTabWidth(iframeWidth);
    } else {
      changeTabWidth(WIDGET_DEFAULT_WIDTH);
    }
  }, [changeTabWidth, currentTab, shouldSelectProvider, tab]);

  const startWidgetListener = useCallback(
    (smartAppUrl: string) => {
      widgetListener({
        onSuccess: (data: any, successLaunchId?: string) => {
          priorAuthLogger.info(`UHC smart app [${tab}] finished successfully`, {
            launchId: successLaunchId,
            ...logContext,
            finishReason: data.OutcomeDesc,
            postMessageData: data,
            noPHI: true,
          });
          setFlowSuccess(true);
        },
        onFail: (data: any, failureLaunchId?: string) => {
          priorAuthLogger.error(`POCA postMessage response - UHC smart app [${tab}] failure`, {
            launchId: failureLaunchId,
            ...logContext,
            postMessageData: data,
            noPHI: true,
          });
          analyticsClient.trackWithContext('got_close_request_from_smartApp', {
            launchId: failureLaunchId,
            reason: data.OutcomeDesc,
            status: SMARTAppStatus.Failure,
          });
          setLoading(false);
          setHasError(true);
        },
        options: { smartAppUrl, tab },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tab, currentContext],
  );

  const callUpdateEligibility = useCallback(
    async (context: CurrentContext): Promise<CurrentContext | undefined> => {
      setLoading(true);
      const updatedContext = await updateEligibility(context);
      setLoading(false);
      return updatedContext;
    },
    [updateEligibility],
  );
  const { deviceId, user, organization } = useVimUserMetadata();

  const launch = useCallback(
    async (context: CurrentContext) => {
      setLoading(true);
      setHasError(false);
      const launchContext = filterLogContext(context);
      try {
        priorAuthLogger.info(`UHC smart app [${tab}] start launching`, {
          ...launchContext,
          deviceId,
          noPHI: true,
        });
        void analyticsClient.trackWithContext('smart_app_start_launching', {
          searchedIcds: context.searchedIcds,
          searchedCpts: context.searchedCpts,
        });
        const pubsubId = window.btoa(`${user?.identifiers?.vimUserID}`);
        const launchUrl = await getSmartAppLaunchUrl({
          smartApp: tabToSmartApp[tab],
          pubsubId,
          deviceId: `${deviceId}`,
          context: {
            ...context,
          },
        });

        if (!launchUrl) {
          throw new Error(`POCA postMessage response - UHC smart app [${tab}] missing launch url`);
        }

        const launchIdParam: string | undefined = getLaunchId(launchUrl);
        priorAuthLogger.info(`UHC smart app [${tab}] launch data`, {
          ...launchContext,
          launchId: launchIdParam,
          launchUrl,
          noPHI: true,
        });
        setLaunchId(launchIdParam);
        startWidgetListener(launchUrl);
        setIframeUrl(launchUrl);
        // PACS is the only SMART app this wrapper launches, and its submit flow needs pub/sub.
        void initPubSubService({ pubsubId, organizationId: organization?.identifiers?.id });

        void analyticsClient.trackWithContext('finish_launch_smartApp', {
          launchId: launchIdParam,
          status: SMARTAppStatus.Success,
          searchedIcds: context.searchedIcds,
          searchedCpts: context.searchedCpts,
          vim_patient_id: context.patient?.identifiers?.vimPatientId,
          content_supplier_patient_id: getContentSupplierId(context),
        });
      } catch (error) {
        priorAuthLogger.error(`POCA postMessage response - UHC smart app [${tab}] launch failed`, {
          ...launchContext,
          error,
          noPHI: true,
        });
        void analyticsClient.trackWithContext('finish_launch_smartApp', {
          status: SMARTAppStatus.Failure,
          searchedIcds: context.searchedIcds,
          searchedCpts: context.searchedCpts,
          vim_patient_id: context.patient?.identifiers?.vimPatientId,
        });
        setHasError(true);
      } finally {
        updateLaunchedComponents({ [tab]: true });
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startWidgetListener, tab, updateLaunchedComponents, deviceId],
  );

  const displayError = hasError && !loading;
  useEffect(() => {
    if (displayError) {
      void analyticsClient.trackWithContext('display_error_screen', {
        launchId,
      });
      void priorAuthLogger.info(`UHC smart app [${tab}] - Error screen display`, {
        launchId,
        ...logContext,
        noPHI: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayError, currentContext, tab, launchId]);

  useEffect(() => {
    // Hide button's loader if selected provider's eligibility failed
    if (isEligibilityFailed && loading) {
      setLoading(false);
    }
  }, [isEligibilityFailed, loading]);

  const resubmit = () => {
    priorAuthLogger.info(`UHC smart app [${tab}] - Retry button in Vim's error screen clicked`, {
      launchId,
      ...logContext,
      noPHI: true,
    });
    void analyticsClient.trackWithContext('retry_button_in_Vim_error_screen_clicked', {
      launchId,
    });
    void launch(currentContext);
  };

  const cancel = () => {
    priorAuthLogger.info(`UHC smart app [${tab}] - Cancel button in Vim's error screen clicked`, {
      launchId,
      ...logContext,
      noPHI: true,
    });
    void analyticsClient.trackWithContext('cancel_button_in_Vim_error_screen_clicked', {
      launchId,
    });
    resetState();
    if (!displaySelectProviderScreen) {
      goToTab(Tab.PriorAuthInquiry);
    }
  };

  const reset = () => {
    resetState();
    priorAuthLogger.info(`UHC smart app [${tab}] - Reset button clicked`, {
      launchId,
      ...logContext,
      noPHI: true,
    });
    void analyticsClient.trackWithContext('reset_button_clicked', {
      launchId,
    });
  };

  const done = () => {
    resetState();
    goToTab(Tab.PriorAuthInquiry);
    priorAuthLogger.info(`UHC smart app [${tab}] - Done button clicked`, {
      launchId,
      ...logContext,
      noPHI: true,
    });
    void analyticsClient.trackWithContext('done_button_clicked', {
      launchId,
    });
  };

  const onConfirm = async (provider: Provider) => {
    const context = confirmSelectedProvider(provider);
    const updatedContext = await callUpdateEligibility(context);
    if (updatedContext) {
      void launch(updatedContext);
    }
    setShouldSelectProvider(false);
  };

  return (
    <>
      <div
        style={
          currentTab !== tab || !shouldSelectProvider || !shouldRelaunch ? { display: 'none' } : {}
        }
      >
        <SelectProvider
          isLoading={loading}
          onConfirm={onConfirm}
          setSelectedProvider={setSelectedProvider}
          selectedProvider={selectedProvider}
          currentTab={currentTab}
        />
      </div>
      <div style={currentTab !== tab || shouldSelectProvider ? { display: 'none' } : {}}>
        {loading ? (
          <Loader size="default" type="dots" />
        ) : displayError ? (
          <ErrorScreen onCancel={cancel} onResubmit={resubmit} />
        ) : (
          <>
            <iframe title="submit-case-iframe" src={iframeUrl} className="uhc-iframe-wrapper" />
            {flowSuccess ? (
              <ActionButton text="Done" onClick={done} />
            ) : (
              <ActionButton text="Reset" onClick={reset} />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default UhcSmartAppWrapper;
